import React, { useCallback, useEffect, useMemo, useState } from "react";
import useApiStable from "../../utils/useApiStable";

const PlayerManagement = ({ onPlayersUpdated }) => {
  const api = useApiStable();
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const [newPlayerFirstName, setNewPlayerFirstName] = useState("");
  const [newPlayerLastName, setNewPlayerLastName] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [editId, setEditId] = useState(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchPlayers = useCallback(() => {
    api
      .get("/team-players")
      .then((data) => setPlayers(Array.isArray(data) ? data : []))
      .catch(() => setPlayers([]));
  }, [api]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const notifyPlayersUpdated = useCallback(() => {
    if (onPlayersUpdated) {
      onPlayersUpdated();
    }
  }, [onPlayersUpdated]);

  const formatPlayerSubtitle = (player) => {
    if (!player?.personId) {
      return "Nicht mit einer Person verknüpft";
    }
    return player.email || `Person #${player.personId}`;
  };

  const playerOptions = useMemo(
    () =>
      players
        .map((player) => ({
          ...player,
          optionLabel: player.personId
            ? `${player.name} — ${player.email || `Person #${player.personId}`}`
            : player.name,
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")),
    [players]
  );

  const createPlayer = async () => {
    const trimmedFirst = newPlayerFirstName.trim();
    const trimmedLast = newPlayerLastName.trim();
    if (!trimmedFirst || !trimmedLast) return;

    setIsSaving(true);
    try {
      await api.post("/team-players", { firstName: trimmedFirst, lastName: trimmedLast });
      setNewPlayerFirstName("");
      setNewPlayerLastName("");
      fetchPlayers();
      notifyPlayersUpdated();
    } catch (err) {
      alert(err.message || "Spieler konnte nicht angelegt werden.");
    } finally {
      setIsSaving(false);
    }
  };

  const startEditPlayer = (player) => {
    setEditId(player.id);
    setEditFirstName(player.firstName || "");
    setEditLastName(player.lastName || "");
  };

  const cancelEditPlayer = () => {
    setEditId(null);
    setEditFirstName("");
    setEditLastName("");
  };

  const saveEditPlayer = async (id) => {
    const trimmedFirst = editFirstName.trim();
    const trimmedLast = editLastName.trim();
    if (!trimmedFirst || !trimmedLast) return;

    setIsSaving(true);
    try {
      const updated = await api.put(`/team-players/${id}`, {
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });
      setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, ...updated } : p)));
      cancelEditPlayer();
      notifyPlayersUpdated();
    } catch (err) {
      alert(err.message || "Spieler konnte nicht umbenannt werden.");
    } finally {
      setIsSaving(false);
    }
  };

  const deletePlayer = async (id) => {
    if (!window.confirm("Soll dieser Spieler wirklich gelöscht werden?")) {
      return;
    }

    setIsSaving(true);
    try {
      await api.delete(`/team-players/${id}`);
      setPlayers((prev) => prev.filter((p) => p.id !== id));
      notifyPlayersUpdated();
    } catch (err) {
      alert(err.message || "Spieler konnte nicht gelöscht werden.");
    } finally {
      setIsSaving(false);
    }
  };

  const mergePlayers = async () => {
    const sourceId = Number(mergeSourceId);
    const targetId = Number(mergeTargetId);

    if (!sourceId || !targetId) {
      alert("Bitte Quell- und Ziel-Spieler auswählen.");
      return;
    }

    if (sourceId === targetId) {
      alert("Bitte zwei unterschiedliche Spieler auswählen.");
      return;
    }

    const sourcePlayer = players.find((player) => player.id === sourceId);
    const targetPlayer = players.find((player) => player.id === targetId);
    const confirmed = window.confirm(
      `Soll "${sourcePlayer?.name || sourceId}" in "${targetPlayer?.name || targetId}" zusammengelegt werden?\n\n` +
        "Der Quell-Spieler wird gelöscht. Team-Zuordnungen bleiben erhalten und werden auf den Ziel-Spieler umgebogen."
    );
    if (!confirmed) {
      return;
    }

    setIsSaving(true);
    try {
      await api.post("/team-players/merge", {
        sourceTeamPlayerId: sourceId,
        targetTeamPlayerId: targetId,
      });
      setMergeSourceId("");
      setMergeTargetId("");
      fetchPlayers();
      notifyPlayersUpdated();
    } catch (err) {
      alert(err.message || "Spieler konnten nicht zusammengelegt werden.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="adminpanel-card">
      <div className="adminpanel-card-header">
        <div>
          <h2>Spielerpool</h2>
          <p className="adminpanel-page-description">
            TeamPlayer werden hier zentral gepflegt. Bei verknüpften Personen wird zusätzlich die E-Mail-Adresse angezeigt.
          </p>
        </div>
      </div>

      <div className="adminpanel-inline-row" style={{ marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
        <input
          type="text"
          placeholder="Vorname*"
          value={newPlayerFirstName}
          onChange={(e) => setNewPlayerFirstName(e.target.value)}
          className="groupphase-input"
          disabled={isSaving}
        />
        <input
          type="text"
          placeholder="Nachname*"
          value={newPlayerLastName}
          onChange={(e) => setNewPlayerLastName(e.target.value)}
          className="groupphase-input"
          disabled={isSaving}
        />
        <button
          onClick={createPlayer}
          className="groupphase-button"
          disabled={isSaving || !newPlayerFirstName.trim() || !newPlayerLastName.trim()}
        >
          Anlegen
        </button>
      </div>

      <div className="adminpanel-card" style={{ marginBottom: 18, background: "#f8fbff" }}>
        <h2 style={{ marginBottom: 8 }}>Spieler zusammenlegen</h2>
        <p className="adminpanel-page-description" style={{ marginBottom: 14 }}>
          Nutzen Sie dies für Dubletten. Team-Zuordnungen des Quell-Spielers werden auf den Ziel-Spieler übertragen.
        </p>
        <div className="adminpanel-form">
          <label className="adminpanel-field">
            <span>Quell-Spieler</span>
            <select
              value={mergeSourceId}
              onChange={(e) => setMergeSourceId(e.target.value)}
              className="groupphase-select"
              disabled={isSaving}
            >
              <option value="">Spieler wählen ...</option>
              {playerOptions.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.optionLabel}
                </option>
              ))}
            </select>
          </label>
          <label className="adminpanel-field">
            <span>Ziel-Spieler</span>
            <select
              value={mergeTargetId}
              onChange={(e) => setMergeTargetId(e.target.value)}
              className="groupphase-select"
              disabled={isSaving}
            >
              <option value="">Spieler wählen ...</option>
              {playerOptions
                .filter((player) => String(player.id) !== String(mergeSourceId))
                .map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.optionLabel}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <div className="adminpanel-inline-row" style={{ marginTop: 14, marginBottom: 0 }}>
          <button onClick={mergePlayers} className="groupphase-button" disabled={isSaving || !mergeSourceId || !mergeTargetId}>
            Zusammenlegen
          </button>
        </div>
      </div>

      <input
        type="text"
        placeholder="Suche nach Name oder E-Mail …"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 12px",
          marginBottom: "12px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          fontSize: "0.95em",
          boxSizing: "border-box",
        }}
      />

      <ul className="adminpanel-list" style={{ maxHeight: "none" }}>
        {players
          .filter((p) => {
            if (!search) return true;
            const q = search.toLowerCase();
            return (
              (p.name || "").toLowerCase().includes(q) ||
              (p.email || "").toLowerCase().includes(q)
            );
          })
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"))
          .map((player) => (
          <li key={player.id} className="adminpanel-list-item" style={{ alignItems: "flex-start" }}>
            {editId === player.id ? (
              <>
                <div className="adminpanel-inline-row" style={{ marginBottom: 0, flexWrap: "wrap", gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Vorname*"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="groupphase-input"
                    disabled={isSaving}
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="Nachname*"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="groupphase-input"
                    disabled={isSaving}
                  />
                </div>
                <div className="adminpanel-actions">
                  <button
                    onClick={() => saveEditPlayer(player.id)}
                    className="groupphase-button"
                    disabled={isSaving || !editFirstName.trim() || !editLastName.trim()}
                  >
                    Speichern
                  </button>
                  <button onClick={cancelEditPlayer} className="groupphase-button secondary" disabled={isSaving}>
                    Abbrechen
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <strong>{player.name}</strong>
                  <small style={{ color: "#5a6c7e" }}>{formatPlayerSubtitle(player)}</small>
                </div>
                <div className="adminpanel-actions">
                  <button
                    onClick={() => startEditPlayer(player)}
                    className="groupphase-button"
                    disabled={isSaving}
                  >
                    Umbenennen
                  </button>
                  <button
                    onClick={() => deletePlayer(player.id)}
                    className="delete-button adminpanel-delete-btn"
                    disabled={isSaving}
                  >
                    Löschen
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>

    </section>
  );
};

export default PlayerManagement;
